import classNames from 'classnames';
import { forwardRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import TapFeature from '@alltrails/amplitude/enums/TapFeature';
import logSaveClicked from '@alltrails/amplitude/events/logSaveClicked';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import TrailMetadataStats from '@alltrails/cards/components/TrailMetadataStats';
import { getUrlFromSlug } from '@alltrails/cards/utils/cardLinks';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Link from '@alltrails/denali/components/Link';
import LinkInfoRenderer from '@alltrails/denali/components/LinkInfoRenderer';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { updateActiveTrails } from '@alltrails/maps/redux/reducer';
import { AlgoliaTrail, trailFromAlgoliaTrail } from '@alltrails/trails';
import type { Visibility } from '@alltrails/types/Visibility';
import { useAppDispatch } from '@/utils/redux';
import HeartButtonWrapper from './HeartButtonWrapper';
import styles from './TopResults.module.scss';
import TrailCardShowMoreText from './TrailCardShowMoreText';
import TrailCarousel from './TrailCarousel';

type Props = {
  trail: AlgoliaTrail;
  className?: string;
  ranking: number;
  isFavorite: boolean;
  onFavoriteClick: (itemType: 'trail' | 'map' | 'track', itemId: number, itemPrivacy?: Visibility | undefined) => void;
  isShowMoreClicked?: boolean;
  isTabletOrMobile?: boolean;
  focusable?: boolean;
};

const TopTrailsCard = forwardRef<HTMLDivElement, Props>(
  ({ trail, className, ranking, isFavorite, onFavoriteClick, isShowMoreClicked, isTabletOrMobile, focusable = false }, ref) => {
    const dispatch = useAppDispatch();
    const trailToRender = trailFromAlgoliaTrail(trail);
    const {
      name,
      avgRating,
      defaultActivityStats: { difficulty } = {},
      trailCounts: { reviewCount },
      trailGeoStats: { length, durationMinutes },
      trailDetail
    } = trailToRender;
    const languageRegionCode = useLanguageRegionCode();
    const intl = useIntl();
    const isMobile = useIsMobileSizedScreen();
    const publicBaseUrl = usePublicBaseUrl();
    const href = getUrlFromSlug(`/${trail.slug}`, publicBaseUrl, languageRegionCode);
    const minutes = Number(durationMinutes ?? '0');

    const handleTrailClick = useCallback(
      (tapFeature: TapFeature) => {
        logTrailCardClicked({
          trail_id: String(trail.ID),
          card_location: 'location page',
          detailed_card_location: 'location page list',
          tap_feature: tapFeature
        });
      },
      [trail.ID]
    );

    const handleFavoriteClick = () => {
      logSaveClicked({
        detailed_card_location: 'location page list',
        save_location: 'location page',
        map_id: trail.ID,
        map_type: 'trail'
      });
      onFavoriteClick('trail', trail.ID);
    };

    const handleSetHoveredResult = useCallback(() => {
      if (trail._geoloc !== undefined) {
        dispatch(updateActiveTrails([trail]));
      }
    }, [dispatch, trail]);

    const handleClearHoveredResult = useCallback(() => dispatch(updateActiveTrails(undefined)), [dispatch]);

    const hasDescription = Boolean(trailDetail?.description);

    return (
      <div
        ref={ref}
        role="group"
        aria-label="trail"
        className={classNames(styles.trailCardSnippetContainer, className)}
        title={name}
        {...(focusable && { tabIndex: -1 })}
        {...(!isTabletOrMobile && {
          onMouseEnter: handleSetHoveredResult,
          onMouseLeave: handleClearHoveredResult
        })}
      >
        <div className={styles.resultCardContent}>
          <Link href={href} target="_blank" onClick={() => handleTrailClick('trail image')}>
            <TrailCarousel trail={trailToRender} defaultUrl={!isShowMoreClicked ? trail.profile_photo_url : undefined} />
          </Link>
          <div className={styles.titleAndHeart}>
            <div className={styles.titleAndStats}>
              <div className={styles.rankNameAndHeart}>
                <h3 className={styles.trailCardHeading}>
                  <LinkInfoRenderer
                    linkInfo={{ href, target: '_blank' }}
                    className={styles.resultName}
                    onClick={() => handleTrailClick('trail name')}
                  >
                    <span className={styles.desktopRanking}>#{ranking} - </span>
                    {name}
                  </LinkInfoRenderer>
                </h3>
                {!isTabletOrMobile && <HeartButtonWrapper isFavorite={isFavorite} onClick={handleFavoriteClick} />}
              </div>
              <Link className={styles.resultStatsLink} href={href} target="_blank" title={name}>
                <TrailMetadataStats
                  avgRating={avgRating}
                  compact
                  difficulty={difficulty}
                  durationMinutes={isMobile ? undefined : minutes}
                  durationText={
                    !isMobile && trail?.estimated_time_to_complete
                      ? `${intl.formatMessage({ defaultMessage: ' Est.' })} ${trail.estimated_time_to_complete.value} ${trail.estimated_time_to_complete.unit}`
                      : undefined
                  }
                  isDurationMinutesEstimate
                  lengthMeters={length}
                  reviewCount={isMobile ? undefined : reviewCount}
                  size={isMobile ? 'sm' : 'md'}
                />
              </Link>
              {!isTabletOrMobile && hasDescription && (
                <TrailCardShowMoreText
                  className={styles.trailCardSnippetDescription}
                  description={trailDetail?.description}
                  trailId={trail.ID}
                  trailName={name}
                  trailSlug={trail.slug}
                />
              )}
            </div>
            {isTabletOrMobile && <HeartButtonWrapper isFavorite={isFavorite} onClick={handleFavoriteClick} />}
          </div>
        </div>
        {isTabletOrMobile && hasDescription && (
          <div className={styles.trailCardSnippetDescriptionContainer}>
            <TrailCardShowMoreText
              className={styles.trailCardSnippetDescription}
              description={trailDetail?.description}
              trailId={trail.ID}
              trailName={name}
              trailSlug={trail.slug}
            />
          </div>
        )}
      </div>
    );
  }
);

TopTrailsCard.displayName = 'TopTrailsCard';

export default TopTrailsCard;
